import { useEffect } from 'react';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { useLocation } from 'react-router-dom';
import { SK_PREFIX, stripLocale } from '@/contexts/language/routing';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  noIndex?: boolean;
}

const SITE_NAME = 'Stratodata';
const BASE_URL = 'https://www.stratodata.eu';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;
const DEFAULT_TITLE = 'Stratodata | Trusted Data Foundation for Decisions & AI';

function setMetaProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setMetaName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

// hreflang alternates for pages that exist in both English and Slovak
function setAlternates(enUrl: string | null, skUrl: string | null) {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  if (!enUrl || !skUrl) return;
  [['en', enUrl], ['sk', skUrl], ['x-default', enUrl]].forEach(([lang, href]) => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', lang);
    el.setAttribute('href', href);
    document.head.appendChild(el);
  });
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  noIndex = false,
}) => {
  const { language, isBilingualPage } = useLanguage();
  const { pathname } = useLocation();
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const enPath = canonical ?? stripLocale(pathname);
  const enUrl = `${BASE_URL}${enPath}`;
  const skUrl = `${BASE_URL}${SK_PREFIX}${enPath === '/' ? '' : enPath}`;
  const canonicalUrl = language === 'sk' ? skUrl : canonical ? enUrl : BASE_URL;

  useEffect(() => {
    document.title = fullTitle;
    setMetaName('description', description);
    setCanonical(canonicalUrl);

    setMetaProperty('og:title', fullTitle);
    setMetaProperty('og:description', description);
    setMetaProperty('og:url', canonicalUrl);
    setMetaProperty('og:image', ogImage);
    setMetaProperty('og:type', ogType);
    setMetaProperty('og:locale', language === 'sk' ? 'sk_SK' : 'en_US');
    setAlternates(isBilingualPage ? enUrl : null, isBilingualPage ? skUrl : null);

    setMetaName('twitter:title', fullTitle);
    setMetaName('twitter:description', description);
    setMetaName('twitter:image', ogImage);

    if (noIndex) setMetaName('robots', 'noindex, nofollow');

    return () => {
      // Reset to homepage defaults on unmount
      document.title = DEFAULT_TITLE;
      setCanonical(BASE_URL + '/');
      setMetaProperty('og:url', BASE_URL + '/');
      setMetaProperty('og:title', DEFAULT_TITLE);
      setMetaProperty('og:locale', 'en_US');
      setAlternates(null, null);
      // Don't leak a hidden page's noindex onto the next page in the SPA
      if (noIndex) document.querySelector('meta[name="robots"]')?.remove();
    };
  }, [fullTitle, description, canonicalUrl, enUrl, skUrl, language, isBilingualPage, ogImage, ogType, noIndex]);

  return null;
};

export default SEO;
